import React from 'react';

import { STATIC_PLANS } from '../../data/staticPlans';
import { PlanConfig } from '../../services/subscriptionService';

interface QuickSelectGroupProps {
  creditAmount: number;
  setCreditAmount: (amount: number) => void;
  isProcessing: boolean;
  /** Shows each amount's price under it (the split layout). */
  priceFor?: (amount: number) => React.ReactNode;
}

export const QuickSelectGroup: React.FC<QuickSelectGroupProps> = ({
  creditAmount,
  setCreditAmount,
  isProcessing,
  priceFor,
}) => {
  const quickAmounts = [50, 100, 200];

  return (
    <fieldset className="quick-select-group">
      <legend className="top-up-label">Quick amounts</legend>
      <div className="quick-select-grid">
        {quickAmounts.map((amt) => (
          <button
            key={amt}
            className={`quick-select-card ${creditAmount === amt ? 'active' : ''}`}
            onClick={() => setCreditAmount(amt)}
            disabled={isProcessing}
            aria-pressed={creditAmount === amt}
            type="button"
          >
            <span className="amount">{amt}</span>
            <span className="unit">Credits</span>
            {priceFor && <span className="price">{priceFor(amt)}</span>}
          </button>
        ))}
      </div>
    </fieldset>
  );
};

interface CustomInputGroupProps {
  creditAmount: number;
  setCreditAmount: (amount: number) => void;
  isProcessing: boolean;
  estimatedPrice: React.ReactNode;
  inputId: string;
  /** The estimated cost beside the label; the split layout shows it in its summary. */
  showMeta?: boolean;
}

export const CustomInputGroup: React.FC<CustomInputGroupProps> = ({
  creditAmount,
  setCreditAmount,
  isProcessing,
  estimatedPrice,
  inputId,
  showMeta = true,
}) => (
  <div className="custom-input-group">
    <div className="custom-input-heading">
      <label htmlFor={inputId} className="top-up-label">Credits to add</label>
      {showMeta && (
        <div className="custom-input-meta">
          <div className="price-estimation" aria-live="polite">
            Estimated cost <span className="cost-value">{estimatedPrice}</span>
          </div>
        </div>
      )}
    </div>
    <div className="input-with-button">
      <div className="input-wrapper">
        <input
          id={inputId}
          type="number"
          min="1"
          value={creditAmount}
          onChange={(e) => setCreditAmount(Math.max(1, parseInt(e.target.value) || 0))}
          className="credit-input"
          disabled={isProcessing}
        />
        <span className="input-suffix">Credits</span>
      </div>
    </div>
  </div>
);

export const CreditPurchaseButton: React.FC<{
  creditAmount: number;
  isProcessing: boolean;
  onTopUp: () => void;
}> = ({ creditAmount, isProcessing, onTopUp }) => (
  <button
    className="top-up-button top-up-button--final"
    onClick={onTopUp}
    disabled={isProcessing || creditAmount <= 0}
    aria-busy={isProcessing}
    type="button"
  >
    {isProcessing ? 'Processing...' : `Buy ${creditAmount} credits`}
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  </button>
);

export const SecurePaymentNote: React.FC<{ text: string }> = ({ text }) => (
  <p className="top-up-info">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
    {text}
  </p>
);

export type TopUpFormLayout = 'stacked' | 'split';

type TopUpFormProps = Omit<CustomInputGroupProps, 'showMeta'> & {
  layout: TopUpFormLayout;
  paymentNote: string;
  onBuy: () => void;
  /** Price of a quick amount, shown on its card in the split layout. */
  priceFor?: (amount: number) => React.ReactNode;
};

/** The total and Buy button as one summary panel (the split layout's right column). */
const CreditTopUpSummary: React.FC<TopUpFormProps> = ({
  creditAmount, isProcessing, estimatedPrice, paymentNote, onBuy,
}) => (
  <div className="top-up-summary">
    <div className="top-up-summary__total" aria-live="polite">
      <span className="top-up-label">Estimated cost</span>
      <span className="cost-value">{estimatedPrice}</span>
    </div>
    <CreditPurchaseButton creditAmount={creditAmount} isProcessing={isProcessing} onTopUp={onBuy} />
    <SecurePaymentNote text={paymentNote} />
  </div>
);

/**
 * The web top-up form. Stacked: amount, Buy, quick amounts. Split (a wide dialog):
 * the amount and quick amounts on the left, the summary with the Buy button on the right.
 */
export const TopUpForm: React.FC<TopUpFormProps> = (props) => {
  const { layout, creditAmount, setCreditAmount, isProcessing, paymentNote, onBuy, priceFor } = props;
  const quickSelect = (
    <QuickSelectGroup
      creditAmount={creditAmount}
      setCreditAmount={setCreditAmount}
      isProcessing={isProcessing}
      priceFor={layout === 'split' ? priceFor : undefined}
    />
  );

  if (layout === 'split') {
    return (
      <div className="top-up-split">
        <div className="top-up-split__amount">
          <CustomInputGroup {...props} showMeta={false} />
          {quickSelect}
        </div>
        <CreditTopUpSummary {...props} />
      </div>
    );
  }

  return (
    <>
      <CustomInputGroup {...props} />
      <CreditPurchaseButton creditAmount={creditAmount} isProcessing={isProcessing} onTopUp={onBuy} />
      {quickSelect}
      <SecurePaymentNote text={paymentNote} />
    </>
  );
};

export const getEstimatedPrice = (
  amount: number, 
  curr: string, 
  loading: boolean,
  plans: Record<string, PlanConfig>,
  planId: string
) => {
  if (loading) return '...';

  const plan = plans[planId];
  const rate = curr === 'USD'
    ? (plan?.topup_price_per_credit || STATIC_PLANS.free.topup_price_per_credit || 0)
    : (plan?.topup_price_per_credit_inr || STATIC_PLANS.free.topup_price_per_credit_inr || 0);

  const total = amount * rate;

  if (curr !== 'INR') return `$${total.toFixed(2)}`;

  return (
    <span className="price-estimation-wrapper">
      <span className="main-price">₹{total.toFixed(2)}</span>
      <span className="gst-subtext gst-subtext-inline">
         (Includes 18% GST)
      </span>
    </span>
  );
};
